/**
 * OnboardingPage — 5-step birth data + goal collection form.
 * Steps: Personal info → Birth date/time → Birth city → Goals → Review & confirm
 * Submits to PATCH /v1/users/me and routes to the most relevant feature.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { profileApi, type SetupProfilePayload } from '@/lib/api'
import { useAppStore } from '@/store'
import { cn } from '@/lib/utils'
import { ChevronRight, ChevronLeft, Check, User, Calendar, MapPin, Sparkles, Target } from 'lucide-react'

// ── Types ────────────────────────────────────────────────────────────────────

interface FormData {
  name: string
  birthDate: string
  birthTime: string
  birthPlace: string
  latitude: number | null
  longitude: number | null
  timezone: string
  goals: string[]
}

// ── Goal config ───────────────────────────────────────────────────────────────

interface GoalOption {
  id: string
  label: string
  sanskrit: string
  emoji: string
  route: string   // where to send the user after onboarding
}

const GOAL_OPTIONS: GoalOption[] = [
  { id: 'career_guidance',       label: 'Career',              sanskrit: 'कर्म',      emoji: '💼', route: '/dasha' },
  { id: 'relationship_marriage', label: 'Relationship & Marriage', sanskrit: 'विवाह',  emoji: '💍', route: '/compatibility' },
  { id: 'health',                label: 'Health & Wellbeing',  sanskrit: 'आरोग्य',    emoji: '🌿', route: '/remedies' },
  { id: 'wealth_finance',        label: 'Wealth & Finance',    sanskrit: 'धन',        emoji: '✨', route: '/muhurta' },
  { id: 'education',             label: 'Education',           sanskrit: 'विद्या',    emoji: '📚', route: '/dasha' },
  { id: 'spiritual_growth',      label: 'Spiritual Growth',    sanskrit: 'आध्यात्म',  emoji: '🪷', route: '/birth-chart' },
  { id: 'general_life',          label: 'General Life Guidance', sanskrit: 'जीवन',   emoji: '🌟', route: '/dashboard' },
]

/** Returns the best route given selected goals, prioritised by position in GOAL_OPTIONS. */
function routeForGoals(goals: string[]): string {
  if (goals.length === 0) return '/dashboard'
  const first = GOAL_OPTIONS.find(g => g.id === goals[0])
  return first?.route ?? '/dashboard'
}

// ── Step config ──────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'About You',   icon: User,      sanskrit: 'परिचय' },
  { id: 2, label: 'Birth Time',  icon: Calendar,  sanskrit: 'जन्म समय' },
  { id: 3, label: 'Birth Place', icon: MapPin,    sanskrit: 'जन्म स्थान' },
  { id: 4, label: 'Your Goals',  icon: Target,    sanskrit: 'लक्ष्य' },
  { id: 5, label: 'Review',      icon: Sparkles,  sanskrit: 'समीक्षा' },
]

// ── Step 1: Name ─────────────────────────────────────────────────────────────

function StepName({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-star-white text-xl font-semibold">What's your name?</h2>
        <p className="text-star-muted text-sm mt-1.5">
          Your personalized cosmic readings will use this.
        </p>
      </div>
      <div className="space-y-2">
        <label htmlFor="name" className="text-star-soft text-sm font-medium">Full name</label>
        <input
          id="name"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. Arjun Sharma"
          autoFocus
          className={cn(
            'w-full px-4 py-3 rounded-lg',
            'bg-cosmic-dusk border border-cosmic-mist',
            'text-star-white placeholder:text-star-dim',
            'focus:outline-none focus:border-violet focus:ring-1 focus:ring-violet/30',
            'transition-colors duration-150',
          )}
        />
      </div>
    </div>
  )
}

// ── Step 2: Date + Time ───────────────────────────────────────────────────────

function StepDateTime({
  date, time,
  onDateChange, onTimeChange,
}: {
  date: string; time: string
  onDateChange: (v: string) => void; onTimeChange: (v: string) => void
}) {
  const today = new Date().toISOString().split('T')[0]
  const isFuture = !!date && date > today

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-star-white text-xl font-semibold">When were you born?</h2>
        <p className="text-star-muted text-sm mt-1.5">
          Accurate birth time enables precise Dasha and house calculations.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="birthDate" className="text-star-soft text-sm font-medium">Date of birth</label>
          <input
            id="birthDate"
            type="date"
            value={date}
            onChange={(e) => onDateChange(e.target.value)}
            max={today}
            className={cn(
              'w-full px-4 py-3 rounded-lg',
              'bg-cosmic-dusk border',
              isFuture ? 'border-inauspicious' : 'border-cosmic-mist',
              'text-star-white',
              '[color-scheme:dark]',
              'focus:outline-none focus:border-violet focus:ring-1 focus:ring-violet/30',
              'transition-colors duration-150',
            )}
          />
          {isFuture && (
            <p className="text-inauspicious text-xs mt-1">Birth date cannot be in the future.</p>
          )}
        </div>
        <div className="space-y-2">
          <label htmlFor="birthTime" className="text-star-soft text-sm font-medium">
            Time of birth
            <span className="text-star-dim text-2xs ml-1.5">(as accurate as possible)</span>
          </label>
          <input
            id="birthTime"
            type="time"
            value={time}
            onChange={(e) => onTimeChange(e.target.value)}
            className={cn(
              'w-full px-4 py-3 rounded-lg',
              'bg-cosmic-dusk border border-cosmic-mist',
              'text-star-white',
              '[color-scheme:dark]',
              'focus:outline-none focus:border-violet focus:ring-1 focus:ring-violet/30',
              'transition-colors duration-150',
            )}
          />
        </div>
      </div>
      <p className="text-star-dim text-2xs text-center">
        Don't know the exact time? Enter 12:00 — we'll use a solar chart.
      </p>
    </div>
  )
}

// ── Step 3: Birth place ───────────────────────────────────────────────────────

interface PlaceSuggestion {
  name: string
  lat: number
  lng: number
  timezone: string
}

// Simple geocoding via public nominatim (no API key needed for MVP)
async function searchPlaces(query: string): Promise<PlaceSuggestion[]> {
  if (query.length < 3) return []
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&addressdetails=1&limit=6&featuretype=city`
  const res = await fetch(url, { headers: { 'Accept-Language': 'en' } })
  if (!res.ok) return []
  const data = await res.json() as Array<{
    display_name: string; lat: string; lon: string
  }>
  return data.map(d => ({
    name: d.display_name.split(',').slice(0, 3).join(', '),
    lat: parseFloat(d.lat),
    lng: parseFloat(d.lon),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // rough fallback
  }))
}

function StepBirthPlace({
  place, onSelect, onClear,
}: {
  place: string
  onSelect: (place: string, lat: number, lng: number, tz: string) => void
  onClear: () => void
}) {
  const [query, setQuery] = useState(place)
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState(!!place)

  const handleChange = async (val: string) => {
    setQuery(val)
    setSelected(false)
    onClear()
    if (val.length < 3) { setSuggestions([]); return }
    setLoading(true)
    const results = await searchPlaces(val)
    setSuggestions(results)
    setLoading(false)
  }

  const handlePick = (s: PlaceSuggestion) => {
    setQuery(s.name)
    setSuggestions([])
    setSelected(true)
    onSelect(s.name, s.lat, s.lng, s.timezone)
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-star-white text-xl font-semibold">Where were you born?</h2>
        <p className="text-star-muted text-sm mt-1.5">
          The city of birth determines your Ascendant and house cusps.
        </p>
      </div>
      <div className="relative space-y-2">
        <label htmlFor="birthPlace" className="text-star-soft text-sm font-medium">City of birth</label>
        <div className="relative">
          <input
            id="birthPlace"
            type="text"
            value={query}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="e.g. Mumbai, Maharashtra"
            autoComplete="off"
            className={cn(
              'w-full px-4 py-3 rounded-lg',
              'bg-cosmic-dusk border',
              selected ? 'border-auspicious' : 'border-cosmic-mist',
              'text-star-white placeholder:text-star-dim',
              'focus:outline-none focus:border-violet focus:ring-1 focus:ring-violet/30',
              'transition-colors duration-150',
            )}
          />
          {selected && (
            <Check className="absolute right-3 top-3.5 w-4 h-4 text-auspicious" />
          )}
          {loading && (
            <div className="absolute right-3 top-3.5 w-4 h-4 border-2 border-violet border-t-transparent rounded-full animate-spin" />
          )}
        </div>
        {suggestions.length > 0 && (
          <div className="absolute left-0 right-0 top-full mt-1 z-20 rounded-lg border border-cosmic-mist bg-cosmic-nebula shadow-card overflow-hidden">
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handlePick(s)}
                className="w-full text-left px-4 py-2.5 text-star-soft text-sm hover:bg-cosmic-aurora hover:text-star-white transition-colors"
              >
                <MapPin className="inline-block w-3 h-3 text-star-dim mr-2 shrink-0" />
                {s.name}
              </button>
            ))}
          </div>
        )}
      </div>
      <p className="text-star-dim text-2xs text-center">
        Coordinates and timezone are auto-resolved from the city.
      </p>
    </div>
  )
}

// ── Step 5: Review ────────────────────────────────────────────────────────────

function StepReview({
  data, onEdit,
}: {
  data: FormData
  onEdit: (step: number) => void
}) {
  const rows = [
    { label: 'Name',       value: data.name,       step: 1, icon: '👤' },
    { label: 'Birth date', value: data.birthDate,   step: 2, icon: '📅' },
    { label: 'Birth time', value: data.birthTime || 'Not set', step: 2, icon: '🕐' },
    { label: 'Birth city', value: data.birthPlace,  step: 3, icon: '📍' },
    { label: 'Timezone',   value: data.timezone || 'Auto-detected', step: 3, icon: '🌍' },
  ]

  const selectedGoalLabels = data.goals
    .map(id => GOAL_OPTIONS.find(g => g.id === id)?.label)
    .filter(Boolean)
    .join(', ')

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-star-white text-xl font-semibold">Review your details</h2>
        <p className="text-star-muted text-sm mt-1.5">
          Double-check before we generate your birth chart.
        </p>
      </div>
      <div className="space-y-2">
        {rows.map(({ label, value, step, icon }) => (
          <div
            key={label}
            className="flex items-center justify-between p-3 rounded-lg bg-cosmic-dusk border border-cosmic-mist"
          >
            <div className="flex items-center gap-3">
              <span className="text-base w-5 text-center">{icon}</span>
              <div>
                <p className="text-star-dim text-2xs font-mono uppercase tracking-wider">{label}</p>
                <p className="text-star-bright text-sm font-medium">{value || '—'}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onEdit(step)}
              className="text-gold text-2xs hover:text-gold-bright transition-colors"
            >
              Edit
            </button>
          </div>
        ))}
        {/* Goals row */}
        <div className="flex items-start justify-between p-3 rounded-lg bg-cosmic-dusk border border-cosmic-mist">
          <div className="flex items-start gap-3">
            <span className="text-base w-5 text-center mt-0.5">🎯</span>
            <div>
              <p className="text-star-dim text-2xs font-mono uppercase tracking-wider">Goals</p>
              <p className="text-star-bright text-sm font-medium leading-snug">
                {selectedGoalLabels || '—'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onEdit(4)}
            className="text-gold text-2xs hover:text-gold-bright transition-colors shrink-0 ml-2"
          >
            Edit
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Step 5: Goals ─────────────────────────────────────────────────────────────

function StepGoals({
  selected, onToggle,
}: {
  selected: string[]
  onToggle: (id: string) => void
}) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-star-white text-xl font-semibold">What guides you?</h2>
        <p className="text-star-muted text-sm mt-1.5">
          Select areas you'd like cosmic guidance on. Pick all that apply.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {GOAL_OPTIONS.map((goal) => {
          const isSelected = selected.includes(goal.id)
          return (
            <button
              key={goal.id}
              type="button"
              onClick={() => onToggle(goal.id)}
              aria-pressed={isSelected}
              className={cn(
                'relative flex flex-col items-center gap-1.5 p-3 rounded-xl border text-center',
                'transition-all duration-150 cursor-pointer select-none',
                isSelected
                  ? 'bg-violet/20 border-violet text-star-white shadow-glow-violet'
                  : 'bg-cosmic-dusk border-cosmic-mist text-star-soft hover:border-violet/50 hover:bg-cosmic-aurora',
              )}
            >
              {isSelected && (
                <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-violet flex items-center justify-center">
                  <Check className="w-2.5 h-2.5 text-star-white" />
                </span>
              )}
              <span className="text-xl leading-none" aria-hidden="true">{goal.emoji}</span>
              <span className="text-xs font-medium leading-snug">{goal.label}</span>
              <span className="text-star-dim text-2xs font-mono">{goal.sanskrit}</span>
            </button>
          )
        })}
      </div>
      <p className="text-star-dim text-2xs text-center">
        You can update these anytime in Settings.
      </p>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

const EMPTY_FORM: FormData = {
  name: '',
  birthDate: '',
  birthTime: '',
  birthPlace: '',
  latitude: null,
  longitude: null,
  timezone: '',
  goals: [],
}

function isStepValid(step: number, data: FormData): boolean {
  switch (step) {
    case 1: return data.name.trim().length >= 2
    case 2: return !!data.birthDate && data.birthDate <= new Date().toISOString().split('T')[0]
    case 3: return !!data.birthPlace && data.latitude !== null
    case 4: return data.goals.length >= 1
    case 5: return true
    default: return false
  }
}

export function OnboardingPage() {
  const [step, setStep] = useState(1)
  const [form, setForm] = useState<FormData>(EMPTY_FORM)
  const navigate = useNavigate()
  const setUser = useAppStore((s) => s.setUser)

  const toggleGoal = (id: string) => {
    setForm(f => ({
      ...f,
      goals: f.goals.includes(id) ? f.goals.filter(g => g !== id) : [...f.goals, id],
    }))
  }

  const { mutate: submit, isPending, isError, error } = useMutation({
    mutationFn: () => profileApi.setup({
      displayName: form.name,
      dateOfBirth: form.birthDate,
      timeOfBirth: form.birthTime || '12:00',
      birthCity: form.birthPlace,
      birthLatitude: form.latitude!,
      birthLongitude: form.longitude!,
      birthTimezone: form.timezone,
      goals: form.goals,
    } satisfies SetupProfilePayload),
    onSuccess: (user) => {
      setUser(user)
      navigate(routeForGoals(form.goals), { replace: true })
    },
  })

  const canAdvance = isStepValid(step, form)

  const handleNext = () => {
    if (step < STEPS.length) setStep(s => s + 1)
    else submit()
  }

  return (
    <div className="min-h-screen bg-cosmic-void flex flex-col items-center justify-center px-4 py-12">
      {/* Background glow */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 50% 0%, rgba(107,33,168,0.18) 0%, transparent 70%)',
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-violet-deep to-violet-mid mb-4 shadow-glow-violet">
            <span className="text-gold text-xl">✦</span>
          </div>
          <h1 className="font-display text-2xl font-light text-star-white tracking-wide">
            Set up your chart
          </h1>
          <p className="text-star-dim text-sm mt-1 font-mono">
            Step {step} of {STEPS.length} · {STEPS[step - 1]?.sanskrit}
          </p>
        </div>

        {/* Progress bar */}
        <div className="flex gap-1.5 mb-8">
          {STEPS.map((s) => (
            <div
              key={s.id}
              className={cn(
                'flex-1 h-1 rounded-full transition-all duration-300',
                s.id < step ? 'bg-auspicious' : s.id === step ? 'bg-gold' : 'bg-cosmic-mist',
              )}
            />
          ))}
        </div>

        {/* Step card */}
        <div className="card-cosmic p-6 mb-6">
          {step === 1 && (
            <StepName value={form.name} onChange={(v) => setForm(f => ({ ...f, name: v }))} />
          )}
          {step === 2 && (
            <StepDateTime
              date={form.birthDate}
              time={form.birthTime}
              onDateChange={(v) => setForm(f => ({ ...f, birthDate: v }))}
              onTimeChange={(v) => setForm(f => ({ ...f, birthTime: v }))}
            />
          )}
          {step === 3 && (
            <StepBirthPlace
              place={form.birthPlace}
              onSelect={(place, lat, lng, tz) =>
                setForm(f => ({ ...f, birthPlace: place, latitude: lat, longitude: lng, timezone: tz }))
              }
              onClear={() =>
                setForm(f => ({ ...f, birthPlace: '', latitude: null, longitude: null, timezone: '' }))
              }
            />
          )}
          {step === 4 && (
            <StepGoals selected={form.goals} onToggle={toggleGoal} />
          )}
          {step === 5 && (
            <StepReview data={form} onEdit={(s) => setStep(s)} />
          )}

          {isError && (
            <p className="mt-4 text-inauspicious text-sm text-center">
              {(error as Error)?.message || 'Something went wrong. Please try again.'}
            </p>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-3">
          {step > 1 && (
            <button
              type="button"
              onClick={() => setStep(s => s - 1)}
              disabled={isPending}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium',
                'bg-cosmic-dusk border border-cosmic-mist text-star-soft',
                'hover:bg-cosmic-aurora hover:text-star-bright transition-colors',
                'disabled:opacity-40',
              )}
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
          )}

          <button
            type="button"
            onClick={handleNext}
            disabled={!canAdvance || isPending}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold',
              'transition-all duration-150',
              canAdvance && !isPending
                ? 'bg-gradient-to-r from-violet to-violet-mid text-star-white shadow-glow-violet hover:opacity-90 active:scale-[0.98]'
                : 'bg-cosmic-dusk border border-cosmic-mist text-star-dim cursor-not-allowed',
            )}
          >
            {isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-star-white/30 border-t-star-white rounded-full animate-spin" />
                Calculating chart…
              </>
            ) : step === STEPS.length ? (
              <>
                <Sparkles className="w-4 h-4" />
                Begin my journey
              </>
            ) : (
              <>
                Continue
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>

        {/* Step dots */}
        <div className="flex justify-center gap-2 mt-6">
          {STEPS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => s.id < step && setStep(s.id)}
              className={cn(
                'w-1.5 h-1.5 rounded-full transition-all duration-200',
                s.id === step ? 'bg-gold w-4' : s.id < step ? 'bg-auspicious/60' : 'bg-cosmic-mist',
              )}
              aria-label={`Step ${s.id}: ${s.label}`}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
